import { IntlShape } from "react-intl";
import { FieldValues, UseFormGetValues, UseFormSetError } from "react-hook-form";

import { IFieldProps } from "types/Organisational";
import { isCostRecoveryRequiredUnset } from "@RequestFormV2/sections/Organisation/context/isCostRecoveryRequiredUnset";
import { setRequiredFieldError } from "@RequestFormV2/validation/utils/setFieldErrors";

const SKIPPED_FIELD_IDS = new Set(["invoice_location", "location", "shipment_title", "cost_recovery_required"]);

function getCostRecoveryYesValue(organisationFields: IFieldProps[]): string {
  const costRecoveryField = organisationFields.find((f) => f.field_id === "cost_recovery_required");
  return (
    (costRecoveryField?.dropdown_values as Array<{ key: string; value: string }> | undefined)?.find(
      (item) => item.key === "Yes",
    )?.value ?? "True"
  );
}

function isCostRecoveryVisible(getValues: UseFormGetValues<FieldValues>, organisationFields: IFieldProps[]): boolean {
  if (!organisationFields.length) return false;

  const costRecoveryField = organisationFields.find((f) => f.field_id === "cost_recovery_required");
  if (costRecoveryField?.mandatory) return true;

  const reasonCode = getValues("organizational.reason_code");
  const reasonCodeField = organisationFields.find((f) => f.field_id === "reason_code");
  if (reasonCode && reasonCodeField?.dropdown_values?.length) {
    const selected = (reasonCodeField.dropdown_values as Array<{ value: string; cost_recovery_possible?: boolean }>).find(
      (item) => item.value === reasonCode,
    );
    return !!selected?.cost_recovery_possible;
  }

  return false;
}

function shouldValidateOrganisationField(
  field: IFieldProps,
  getValues: UseFormGetValues<FieldValues>,
  organisationFields: IFieldProps[],
): boolean {
  const fieldId = field.field_id;
  if (!fieldId || SKIPPED_FIELD_IDS.has(fieldId)) return false;

  if (!fieldId.startsWith("custom_cost_recovery_")) return true;

  if (!isCostRecoveryVisible(getValues, organisationFields)) return false;

  const costRecoveryField = organisationFields.find((f) => f.field_id === "cost_recovery_required");
  const yesValue = getCostRecoveryYesValue(organisationFields);
  const costRecoveryRequired = getValues("organizational.cost_recovery_required");

  if (costRecoveryRequired === yesValue) return true;
  if (costRecoveryField?.mandatory && isCostRecoveryRequiredUnset(costRecoveryRequired)) return true;

  return false;
}

type InvoiceLocationDetails = {
  mandatory?: boolean;
};

type ShipmentTitleDetails = {
  mandatory?: boolean;
};

export function validateOrganisation(
  getValues: UseFormGetValues<FieldValues>,
  setError: UseFormSetError<FieldValues>,
  intl: IntlShape,
  options: {
    organisationFields: IFieldProps[];
    invoiceLocationDetails?: InvoiceLocationDetails;
    shipmentTitleDetails?: ShipmentTitleDetails;
    selectedLocation?: string;
  },
): boolean {
  let isValid = true;

  if (!setRequiredFieldError(setError, "organizational.location", getValues("organizational.location"), intl)) {
    isValid = false;
  }

  if (options.selectedLocation && options.invoiceLocationDetails?.mandatory) {
    if (
      !setRequiredFieldError(setError, "organizational.invoice_location", getValues("organizational.invoice_location"), intl)
    ) {
      isValid = false;
    }
  }

  if (options.shipmentTitleDetails?.mandatory) {
    if (!setRequiredFieldError(setError, "shipment.title", getValues("shipment.title"), intl)) {
      isValid = false;
    }
  }

  options.organisationFields.forEach((field) => {
    if (!field.mandatory || !field.field_id) return;
    if (!shouldValidateOrganisationField(field, getValues, options.organisationFields)) return;

    const fieldPath = field.field_id === "shipment_title" ? "shipment.title" : `organizational.${field.field_id}`;
    if (!setRequiredFieldError(setError, fieldPath, getValues(fieldPath), intl)) {
      isValid = false;
    }
  });

  return isValid;
}
